import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, ShoppingCart, TrendingDown, Store, Search,
  ChevronRight, Sparkles, PiggyBank, BarChart3, RefreshCw, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../context/LanguageContext';
import { getShoppingSuggestions, comparePrices, getPriceHistory } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import OCRScannerModal from '../components/OCRScannerModal';

export default function ShoppingPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [totalSavings, setTotalSavings] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showOCR, setShowOCR] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [suggestionsRes, comparisonsRes] = await Promise.all([
        getShoppingSuggestions(),
        comparePrices({})
      ]);
      
      setSuggestions(suggestionsRes.data.suggestions || []);
      setComparisons(comparisonsRes.data.comparisons || []);
      setTotalSavings(comparisonsRes.data.total_potential_savings || 0);
    } catch (error) {
      console.error('Failed to load shopping data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const [priceRes, historyRes] = await Promise.all([
        comparePrices({ product_name: searchQuery }),
        getPriceHistory(searchQuery)
      ]);
      
      setSearchResults({
        comparisons: priceRes.data.comparisons || [],
        history: historyRes.data
      });
    } catch (error) {
      toast.error(t('search_error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {t('smart_shopping')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('price_comparison')}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={loadData}>
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Savings Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <PiggyBank className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">
                    {t('potential_savings')}
                  </p>
                  <p className="text-4xl font-bold">{formatCurrency(totalSavings)}</p>
                  <p className="text-white/70 text-sm">
                    {t('switch_store_savings')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('search_product')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearch}>
                  {t('compare')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Search Results */}
        {searchResults && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {t('results_for_query')} &quot;{searchQuery}&quot;
                </CardTitle>
              </CardHeader>
              <CardContent>
                {searchResults.comparisons.length > 0 ? (
                  <div className="space-y-3">
                    {searchResults.comparisons.map((comp, idx) => (
                      <PriceComparisonCard key={idx} comparison={comp} language={language} />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    {t('not_enough_data_scan')}
                  </p>
                )}
                
                {/* Price History Stats */}
                {searchResults.history?.statistics && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm font-medium mb-2">
                      {t('price_statistics')}
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-green-50 rounded-lg p-2">
                        <p className="text-xs text-muted-foreground">Min</p>
                        <p className="font-bold text-green-600">{formatCurrency(searchResults.history.statistics.min_price)}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2">
                        <p className="text-xs text-muted-foreground">Moy.</p>
                        <p className="font-bold text-blue-600">{formatCurrency(searchResults.history.statistics.average_price)}</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2">
                        <p className="text-xs text-muted-foreground">Max</p>
                        <p className="font-bold text-red-600">{formatCurrency(searchResults.history.statistics.max_price)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* AI Suggestions */}
        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {t('suggestions_for_you')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {suggestions.map((suggestion, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-xl ${
                      suggestion.priority === 'high' 
                        ? 'bg-green-50 border border-green-200' 
                        : suggestion.priority === 'medium'
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-muted/50'
                    }`}
                  >
                    <p className="font-medium">
                      {language === 'fr' ? suggestion.title_fr : language === 'pt' ? (suggestion.title_pt || suggestion.title_en) : suggestion.title_en}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {language === 'fr' ? suggestion.message_fr : language === 'pt' ? (suggestion.message_pt || suggestion.message_en) : suggestion.message_en}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Top Price Comparisons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-green-500" />
                {t('best_savings_opportunities')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparisons.length > 0 ? (
                <div className="space-y-3">
                  {comparisons.slice(0, 10).map((comp, idx) => (
                    <PriceComparisonCard key={idx} comparison={comp} language={language} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">
                    {t('no_data_yet')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('scan_receipts_to_compare')}
                  </p>
                  <Button className="mt-4" onClick={() => setShowOCR(true)}>
                    <Camera className="w-4 h-4 mr-2" />
                    {t('scan_receipt')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-muted/30">
            <CardContent className="p-6">
              <h3 className="font-bold mb-4">
                {t('how_it_works')}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                  <p>{t('scan_receipt_ocr')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                  <p>{t('spenspot_analyzes_prices')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</div>
                  <p>{t('compare_prices_save')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
      
      {/* OCR Scanner Modal */}
      <OCRScannerModal 
        open={showOCR} 
        onClose={() => setShowOCR(false)} 
        onSuccess={() => {
          setShowOCR(false);
          loadData(); // Refresh data after adding transaction
          toast.success(t('receipt_scanned'));
        }}
      />
    </div>
  );
}

function PriceComparisonCard({ comparison, language }) {
  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{comparison.product_name}</p>
        <div className="flex items-center gap-2 mt-1 text-sm">
          <span className="text-red-500 line-through">{formatCurrency(comparison.your_price)}</span>
          <span className="text-muted-foreground">→</span>
          <span className="text-green-600 font-bold">{formatCurrency(comparison.best_price)}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          <Store className="w-3 h-3 inline mr-1" />
          {comparison.best_merchant}
        </p>
      </div>
      <div className="text-right ml-3">
        <p className="text-green-600 font-bold">-{formatCurrency(comparison.potential_savings)}</p>
        <p className="text-xs text-green-600">-{comparison.savings_percentage}%</p>
      </div>
    </div>
  );
}
